import { apiPost } from './apiClient';
import { PushNotificationPayload } from '../types/pushNotification';
import { toast } from '../utils/toast';
import { ServerInfo } from '../models/user';
import i18n from '../i18n';

export interface PushSubscriptionData {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
  unsubscribe?: boolean; // Used for unsubscribe requests
}

/**
 * Service for managing push notification subscriptions
 */
export class PushNotificationService {

  /**
   * Check if push notifications are supported
   */
  static isSupported(): boolean {
    return 'serviceWorker' in navigator && 'PushManager' in window;
  }

  /**
   * Check if the user has granted notification permission
   */
  static hasNotificationPermission(): boolean {
    return Notification.permission === 'granted';
  }

  /**
   * Request notification permission from the user
   */
  static async requestNotificationPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      throw new Error('This browser does not support notifications');
    }

    if (Notification.permission === 'granted') {
      return 'granted';
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      return permission;
    }

    return Notification.permission;
  }

  /**
   * Get the current push subscription if it exists
   */
  static async getCurrentSubscription(): Promise<PushSubscription | null> {
    if (!this.isSupported()) {
      return null;
    }

    const registration = await navigator.serviceWorker.ready;
    return await registration.pushManager.getSubscription();
  }

  /**
   * Create a new push subscription
   */
  static async createSubscription(serverInfo: ServerInfo): Promise<PushSubscription | null> {
    if (!this.isSupported()) {
      throw new Error('Push notifications are not supported');
    }

    if (!this.hasNotificationPermission()) {
      const permission = await this.requestNotificationPermission();
      if (permission !== 'granted') {
        throw new Error('Notification permission denied');
      }
    }

    const  registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      throw new Error('Service worker not registered. Please refresh the page.');
    }
    
    const vapidPublicKey = serverInfo ? serverInfo.vapidPublicKey : null;

    if (!vapidPublicKey) {
      throw new Error('VAPID public key not found in localStorage');
    }

    const arrayBuffer = this.urlBase64ToArrayBuffer(vapidPublicKey);
    
    const subscribeOptions: PushSubscriptionOptions = {
      userVisibleOnly: true,
      applicationServerKey: arrayBuffer
    };

    let subscription = await this.getCurrentSubscription();

    if (!subscription) {
      subscription = await registration.pushManager.subscribe(subscribeOptions);
    }

    return subscription;
  }

  /**
   * Convert a URL-safe base64 string to Uint8Array
   */
  private static urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  /**
   * Convert a URL-safe base64 string to ArrayBuffer for use with Push API
   */
  private static urlBase64ToArrayBuffer(base64String: string): ArrayBuffer {
    const uint8Array = this.urlBase64ToUint8Array(base64String);
    // Create a new ArrayBuffer and copy the data
    const buffer = new ArrayBuffer(uint8Array.length);
    const view = new Uint8Array(buffer);
    view.set(uint8Array);
    return buffer;
  }

  /**
   * Convert PushSubscription to the format expected by the backend
   */
  static formatSubscriptionForBackend(subscription: PushSubscription, unsubscribe: boolean): PushSubscriptionData {
    const keys = subscription.getKey ? {
      p256dh: subscription.getKey('p256dh') ? this.arrayBufferToBase64(subscription.getKey('p256dh')!) : '',
      auth: subscription.getKey('auth') ? this.arrayBufferToBase64(subscription.getKey('auth')!) : ''
    } : { p256dh: '', auth: '' };

    return {
      endpoint: subscription.endpoint,
      keys,
      unsubscribe: unsubscribe
    };
  }

  /**
   * Convert ArrayBuffer to base64 string
   */
  private static arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  /**
   * Send subscription data to the backend
   */
  static async sendSubscriptionToBackend(subscriptionData: PushSubscriptionData): Promise<void> {
    try {
      const response = await apiPost(`${process.env.REACT_APP_API_URL}/api/v1/users/messaging`, subscriptionData);
      if (!response.ok) {
        throw new Error(`Failed to send subscription to backend: ${response.status}`);
      }
    } catch (error) {
      toast.error((i18n.t as any)('Error sending subscription to backend'));
      throw error;
    }
  }

  /**
   * Subscribe to push notifications and register with backend
   */
  static async subscribeToNotifications(serverInfo: ServerInfo): Promise<boolean> {
    try {
      if (!this.isSupported()) {
        return false;
      }

      // Create new subscription
      const subscription = await this.createSubscription(serverInfo);
      if (!subscription) {
        throw new Error('Failed to create push subscription');
      }

      // Format and send to backend
      const subscriptionData = this.formatSubscriptionForBackend(subscription, false);
      await this.sendSubscriptionToBackend(subscriptionData);

      return true;
    } catch (_) {
      return false;
    }
  }

  /**
   * Unsubscribe from push notifications
   */
  static async unsubscribeFromNotifications(): Promise<boolean> {
    try {
      const subscription = await this.getCurrentSubscription();
      
      if (subscription) {
        const subscriptionData = this.formatSubscriptionForBackend(subscription, true);
        await this.sendSubscriptionToBackend(subscriptionData);
        const success = await subscription.unsubscribe();
        return success;
      }
      
      return true; // Already unsubscribed
    } catch (_) {
      return false;
    }
  }

  /**
   * Create a test notification with custom payload (for development)
   */
  static createTestNotification(payload: PushNotificationPayload): void {
    if (!this.hasNotificationPermission()) {
      return;
    }

    new Notification(payload.title, {
      body: payload.body,
      icon: payload.icon || '/logo.png',
      badge: payload.badge || '/logo.png',
      tag: payload.tag || 'test-notification',
      requireInteraction: payload.requireInteraction || false,
      data: payload.data || {}
    });
  }
}
